import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  Button,
  CircularProgress,
  MenuItem,
  TextField,
  Tooltip,
} from "@mui/material";
import BuildIcon from "@mui/icons-material/Build";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import CloseIcon from "@mui/icons-material/Close";
import { selectUser } from "../../../features/userSlice";
import { branches } from "../../WebleadsV2/utilities";
import { tax as serviceStates } from "../../../components/Forms/SelectValues/values";
import { MANUFACTURERS } from "../../../components/WindowDoor/ServiceRequests/constants";
import { submitServiceRequest } from "../../../components/WindowDoor/ServiceRequests/api";
import { IconTile, PageHeader, PageShell } from "../../../components/UI/Page";

// Server caps uploads at 10 files / 64MB each (multer config); mirror it here
// so people find out before they wait through an upload.
const MAX_FILES = 10;
const MAX_FILE_BYTES = 64 * 1024 * 1024;

// The old PHP form billed to one of these. Distinct values in the imported
// history are the authority — if the MySQL export turns up others, add them.
const BILL_TO_OPTIONS = [
  "Contractor",
  "Homeowner",
  "Big C Lumber",
  "Manufacturer",
  "Warranty",
];

// Autosave key. A service request can take a while to write up (the issue
// description especially); a reload or an accidental back should not cost it.
const DRAFT_KEY = "windowServiceRequestDraft";

// Format US phone numbers as (555) 123-4567 while typing. Anything past ten
// digits is dropped so a stray paste can't produce a malformed number.
const formatPhone = (input) => {
  const digits = String(input || "").replace(/\D/g, "").slice(0, 10);
  if (digits.length < 4) return digits;
  if (digits.length < 7) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
};

const EMPTY = {
  filled_out_by: "",
  salesperson_email: "",
  sales_number: "",
  manufacturer: "",
  manufacturer_other: "",
  home_occupied: "",
  homeowner_name: "",
  lot: "",
  address: "",
  city: "",
  state: "",
  zip: "",
  preferred_contact: "",
  customer_phone: "",
  customer_email: "",
  contractor: "",
  age_of_product: "",
  contractor_office_phone: "",
  contractor_cell_phone: "",
  contractor_email: "",
  describe_issue: "",
  cig_number: "",
  product_id: "",
  bill_to: "",
  location: "",
  account_number: "",
  job_number: "",
};

const Field = ({ label, required, hint, children }) => (
  <div className="space-y-1.5">
    <p className="text-sm font-medium">
      {label}
      {required && (
        <span className="ml-0.5 text-rose-500 dark:text-rose-400">*</span>
      )}
    </p>
    {children}
    {hint && <p className="app-muted text-xs">{hint}</p>}
  </div>
);

// A titled group of fields: its own card on the page, or a divided block
// when the form is embedded in a host dialog (so the dialog's glass shows).
const Section = ({ title, description, plain, children }) => (
  <section
    className={
      plain
        ? "app-divider space-y-4 border-t px-6 py-5"
        : "app-card space-y-4 rounded-2xl border p-5 sm:p-6"
    }
  >
    {title && (
      <div>
        <h2 className="text-base font-semibold">{title}</h2>
        {description && (
          <p className="app-muted mt-0.5 text-sm">{description}</p>
        )}
      </div>
    )}
    {children}
  </section>
);

const Choice = ({ checked, onChange, label, name }) => (
  <label className="flex min-h-[2.25rem] cursor-pointer items-center gap-2 text-sm">
    <input
      type="radio"
      name={name}
      checked={checked}
      onChange={onChange}
      className="h-4 w-4 cursor-pointer accent-sky-600"
    />
    <span>{label}</span>
  </label>
);

/**
 * `manual` renders the manager-entry variant (route:
 * /windowdoor/service-requests/new): for phoned-in or paper requests the
 * shop enters itself. Same form, but salesperson fields aren't prefilled
 * from the logged-in manager, validation only demands what a phone call
 * reliably yields, and success lands on the shop's detail page.
 */
/**
 * `embedded` + `onCreated` render the form for a host dialog (the manager
 * dashboard's New Request modal): no page chrome, and success is handed to
 * the host instead of the built-in confirmation screen.
 */
const WindowServiceRequestForm = ({
  manual = false,
  embedded = false,
  onCreated = null,
}) => {
  const user = useSelector(selectUser);
  const navigate = useNavigate();
  const draftKey = manual ? `${DRAFT_KEY}Manual` : DRAFT_KEY;

  const [values, setValues] = useState(EMPTY);
  const [files, setFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(null); // { id, entry_number }
  const [error, setError] = useState(null);
  const [draftRestored, setDraftRestored] = useState(false);

  // Restore a draft first, then fill the submitter's own details into
  // whatever is still blank.
  useEffect(() => {
    let restored = null;
    try {
      const saved = window.localStorage.getItem(draftKey);
      if (saved) {
        restored = JSON.parse(saved);
        setDraftRestored(true);
      }
    } catch (err) {
      // Corrupt or unavailable storage — just start clean.
    }
    setValues((prev) => ({
      ...prev,
      ...(restored || {}),
      // Manual entries are on someone else's behalf — never prefill the
      // manager's own identity into the salesperson fields.
      filled_out_by:
        restored?.filled_out_by || (manual ? "" : user?.name || ""),
      salesperson_email:
        restored?.salesperson_email || (manual ? "" : user?.email || ""),
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.name, user?.email, manual]);

  useEffect(() => {
    if (submitted) return;
    try {
      window.localStorage.setItem(draftKey, JSON.stringify(values));
    } catch (err) {
      // Storage full or blocked; autosave is a convenience, not a promise.
    }
  }, [values, submitted]);

  const set = (field) => (event) =>
    setValues((prev) => ({ ...prev, [field]: event.target.value }));

  const setPhone = (field) => (event) =>
    setValues((prev) => ({ ...prev, [field]: formatPhone(event.target.value) }));

  const setValue = (field, value) =>
    setValues((prev) => ({ ...prev, [field]: value }));

  const occupied = values.home_occupied === "Yes";
  const isAndersen = values.manufacturer === "Andersen";

  const missing = useMemo(() => {
    const gaps = [];
    if (manual) {
      // A phoned-in request reliably yields an issue, a name, and a number —
      // demand those, let the shop fill the rest as it's learned.
      if (!String(values.describe_issue || "").trim()) {
        gaps.push("Describe Issue");
      }
      if (
        !String(values.homeowner_name || "").trim() &&
        !String(values.contractor || "").trim()
      ) {
        gaps.push("Homeowner or Contractor name");
      }
      if (
        !String(values.customer_phone || "").trim() &&
        !String(values.contractor_cell_phone || "").trim() &&
        !String(values.contractor_office_phone || "").trim() &&
        !String(values.customer_email || "").trim()
      ) {
        gaps.push("A phone number or email");
      }
      return gaps;
    }
    const need = {
      filled_out_by: "Filled Out By",
      salesperson_email: "Salesperson Email",
      sales_number: "Sales Number",
      home_occupied: "Is Home Occupied?",
      address: "Address",
      city: "City",
      state: "State",
      zip: "Zip Code",
      preferred_contact: "Preferred contact method",
      contractor: "Contractor",
      age_of_product: "Age of Product",
      describe_issue: "Describe Issue",
      bill_to: "Bill To",
    };
    Object.entries(need).forEach(([field, label]) => {
      if (!String(values[field] || "").trim()) gaps.push(label);
    });
    if (!values.manufacturer) gaps.push("Manufacturer");
    if (values.manufacturer === "Other" && !values.manufacturer_other.trim()) {
      gaps.push("Manufacturer (other)");
    }
    if (occupied && !values.homeowner_name.trim()) gaps.push("Homeowners Name");
    return gaps;
  }, [values, occupied, manual]);

  const addFiles = (event) => {
    const picked = Array.from(event.target.files || []);
    event.target.value = "";
    if (!picked.length) return;
    setError(null);

    const room = MAX_FILES - files.length;
    if (picked.length > room) {
      setError(`You can attach up to ${MAX_FILES} files.`);
    }
    const tooBig = picked.find((file) => file.size > MAX_FILE_BYTES);
    if (tooBig) {
      setError(`"${tooBig.name}" is over the 64 MB limit.`);
    }
    setFiles((prev) => [
      ...prev,
      ...picked
        .slice(0, Math.max(0, room))
        .filter((file) => file.size <= MAX_FILE_BYTES),
    ]);
  };

  const removeFile = (index) =>
    setFiles((prev) => prev.filter((_, i) => i !== index));

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError(null);

    if (missing.length) {
      setError(`Still needed: ${missing.join(", ")}.`);
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        ...values,
        // "Other" is a free-text manufacturer; send what they typed so the
        // shop sees a real name, not the word "Other".
        manufacturer:
          values.manufacturer === "Other"
            ? values.manufacturer_other.trim()
            : values.manufacturer,
        // Nobody lives there, so there is no homeowner to name. Manual
        // entries keep whatever was typed — occupancy is often unknown on a
        // phone call.
        homeowner_name:
          manual || occupied ? values.homeowner_name : "",
        product_id: isAndersen ? values.product_id : "",
        ...(manual ? { manual: "1" } : {}),
      };
      delete payload.manufacturer_other;

      const result = await submitServiceRequest(payload, files, user);
      try {
        window.localStorage.removeItem(draftKey);
      } catch (err) {
        // Nothing to do; the draft will be overwritten by the next form.
      }
      if (onCreated) {
        onCreated(result);
        return;
      }
      setSubmitted(result);
    } catch (err) {
      setError(err.message || "Could not submit the request — please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const startAnother = () => {
    setValues({
      ...EMPTY,
      filled_out_by: user?.name || "",
      salesperson_email: user?.email || "",
    });
    setFiles([]);
    setSubmitted(null);
    setDraftRestored(false);
  };

  if (submitted) {
    return (
      <PageShell size="sm" className="sm:py-12">
        <div className="app-card flex flex-col items-center rounded-2xl border px-6 py-10 text-center sm:px-10">
          <span className="app-icon-tile h-14 w-14 rounded-full bg-emerald-500/15 text-emerald-600 dark:text-emerald-300">
            <TaskAltIcon fontSize="large" />
          </span>
          <h1 className="mt-4 text-2xl font-bold tracking-tight">
            Service request #{submitted.entry_number} submitted
          </h1>
          <p className="app-muted mt-2 max-w-md text-sm leading-relaxed">
            {manual
              ? "The request is in the queue — open it to assign a technician and start the checklist."
              : "The window/door shop has been notified. You can follow its progress under My Service Requests, and you can message the shop on the request itself."}
          </p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <Button
              variant="contained"
              onClick={() =>
                navigate(
                  manual
                    ? `/windowdoor/service-requests/${submitted.id}`
                    : `/windowdoor/my-service-requests/${submitted.id}`
                )
              }
            >
              View Request
            </Button>
            <Button variant="outlined" onClick={startAnother}>
              Submit Another
            </Button>
          </div>
        </div>
      </PageShell>
    );
  }

  const title = manual ? "Manual Service Request Entry" : "Window Service Request";
  const description = manual
    ? "Enter a request that came in by phone or on paper. Only the issue, a name, and a contact are required — fill in the rest as you learn it."
    : "Send a window or door service issue to the shop. Your answers are kept in this browser as you type, so you won't lose them.";
  const plain = embedded;

  const formCard = (
    <form onSubmit={handleSubmit} className={embedded ? "" : "space-y-5"}>
      {embedded && (
        <div className="flex items-start gap-4 px-6 py-5">
          <IconTile icon={BuildIcon} tone="teal" />
          <div className="min-w-0">
            <h1 className="text-xl font-bold tracking-tight">{title}</h1>
            <p className="app-muted mt-0.5 text-sm">{description}</p>
          </div>
        </div>
      )}

      {draftRestored && (
        <div className={embedded ? "px-6 pb-5" : ""}>
          <div
            role="status"
            className="rounded-xl bg-sky-500/10 px-4 py-3 text-sm text-sky-800 dark:text-sky-200"
          >
            Picked up where you left off. Use "Clear form" below to start
            over.
          </div>
        </div>
      )}

      {/* Who's asking */}
      <Section plain={plain} title="Salesperson">
        <div className="grid gap-4 sm:grid-cols-3">
          <Field label="Filled Out By" required>
            <TextField
              fullWidth
              size="small"
              value={values.filled_out_by}
              onChange={set("filled_out_by")}
              inputProps={{ "aria-label": "Filled Out By" }}
            />
          </Field>
          <Field label="Salesperson Email" required>
            <TextField
              fullWidth
              size="small"
              type="email"
              value={values.salesperson_email}
              onChange={set("salesperson_email")}
              inputProps={{ "aria-label": "Salesperson Email" }}
            />
          </Field>
          <Field label="Sales Number" required>
            <TextField
              fullWidth
              size="small"
              value={values.sales_number}
              onChange={set("sales_number")}
              inputProps={{ "aria-label": "Sales Number" }}
            />
          </Field>
        </div>
      </Section>

      {/* Manufacturer */}
      <Section plain={plain} title="Product">
        <Field label="Manufacturer" required>
          <div className="grid gap-x-4 gap-y-1 min-[400px]:grid-cols-2 lg:grid-cols-3">
            {MANUFACTURERS.map((make) => (
              <Choice
                key={make}
                name="manufacturer"
                label={make}
                checked={values.manufacturer === make}
                onChange={() => setValue("manufacturer", make)}
              />
            ))}
            <div className="flex min-h-[2.25rem] items-center gap-2 min-[400px]:col-span-2 lg:col-span-3">
              <input
                type="radio"
                name="manufacturer"
                aria-label="Other"
                checked={values.manufacturer === "Other"}
                onChange={() => setValue("manufacturer", "Other")}
                className="h-4 w-4 shrink-0 cursor-pointer accent-sky-600"
              />
              <TextField
                size="small"
                placeholder="Other"
                value={values.manufacturer_other}
                inputProps={{ "aria-label": "Other manufacturer" }}
                onChange={(event) => {
                  setValue("manufacturer_other", event.target.value);
                  if (event.target.value) setValue("manufacturer", "Other");
                }}
              />
            </div>
          </div>
        </Field>
        <div className="grid gap-4 sm:grid-cols-3">
          <Field label="Age of Product" required>
            <TextField
              fullWidth
              size="small"
              placeholder="e.g. 3 years"
              value={values.age_of_product}
              onChange={set("age_of_product")}
              inputProps={{ "aria-label": "Age of Product" }}
            />
          </Field>
          <Field label="CIG #">
            <TextField
              fullWidth
              size="small"
              value={values.cig_number}
              onChange={set("cig_number")}
              inputProps={{ "aria-label": "CIG #" }}
            />
          </Field>
          {isAndersen && (
            <Field label="Product ID" hint="Andersen only.">
              <TextField
                fullWidth
                size="small"
                value={values.product_id}
                onChange={set("product_id")}
                inputProps={{ "aria-label": "Product ID" }}
              />
            </Field>
          )}
        </div>
      </Section>

      {/* Home + homeowner */}
      <Section plain={plain} title="Home & homeowner">
        <div className="grid gap-4 sm:grid-cols-3">
          <Field label="Is Home Occupied?" required>
            <div className="flex flex-wrap gap-x-5">
              {["Yes", "No"].map((option) => (
                <Choice
                  key={option}
                  name="home_occupied"
                  label={option}
                  checked={values.home_occupied === option}
                  onChange={() => setValue("home_occupied", option)}
                />
              ))}
            </div>
          </Field>
          <Field
            label="Homeowners Name"
            required={occupied}
            hint={
              occupied
                ? undefined
                : "Not needed for an unoccupied home."
            }
          >
            <TextField
              fullWidth
              size="small"
              disabled={!occupied}
              value={occupied ? values.homeowner_name : ""}
              onChange={set("homeowner_name")}
              inputProps={{ "aria-label": "Homeowners Name" }}
            />
          </Field>
          <Field label="Lot">
            <TextField
              fullWidth
              size="small"
              value={values.lot}
              onChange={set("lot")}
              inputProps={{ "aria-label": "Lot" }}
            />
          </Field>
        </div>

        {/* Address */}
        <div className="grid gap-4 sm:grid-cols-4">
          <div className="sm:col-span-2">
            <Field label="Address" required>
              <TextField
                fullWidth
                size="small"
                value={values.address}
                onChange={set("address")}
                inputProps={{ "aria-label": "Address" }}
              />
            </Field>
          </div>
          <Field label="City" required>
            <TextField
              fullWidth
              size="small"
              value={values.city}
              onChange={set("city")}
              inputProps={{ "aria-label": "City" }}
            />
          </Field>
          <Field label="Zip Code" required>
            <TextField
              fullWidth
              size="small"
              value={values.zip}
              onChange={set("zip")}
              inputProps={{ "aria-label": "Zip Code", inputMode: "numeric" }}
            />
          </Field>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="State" required>
            <TextField
              select
              fullWidth
              size="small"
              value={values.state}
              onChange={set("state")}
              SelectProps={{
                displayEmpty: true,
                SelectDisplayProps: { "aria-label": "State" },
              }}
            >
              <MenuItem value="" disabled>
                Select a state...
              </MenuItem>
              {serviceStates.map((state) => (
                <MenuItem key={state} value={state}>
                  {state}
                </MenuItem>
              ))}
            </TextField>
          </Field>
          <Field label="Preferred Contact" required>
            <div className="flex flex-wrap gap-x-5">
              {["Phone", "Email"].map((option) => (
                <Choice
                  key={option}
                  name="preferred_contact"
                  label={option}
                  checked={values.preferred_contact === option}
                  onChange={() => setValue("preferred_contact", option)}
                />
              ))}
            </div>
          </Field>
          <Field
            label="Customer Phone"
            hint="Will be used to contact the customer."
          >
            <TextField
              fullWidth
              size="small"
              type="tel"
              placeholder="(555) 123-4567"
              value={values.customer_phone}
              onChange={setPhone("customer_phone")}
              inputProps={{ "aria-label": "Customer Phone" }}
            />
          </Field>
          <Field label="Customer Email">
            <TextField
              fullWidth
              size="small"
              type="email"
              value={values.customer_email}
              onChange={set("customer_email")}
              inputProps={{ "aria-label": "Customer Email" }}
            />
          </Field>
        </div>
      </Section>

      {/* Contractor */}
      <Section plain={plain} title="Contractor">
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Contractor" required>
            <TextField
              fullWidth
              size="small"
              value={values.contractor}
              onChange={set("contractor")}
              inputProps={{ "aria-label": "Contractor" }}
            />
          </Field>
          <Field label="Contractor Email">
            <TextField
              fullWidth
              size="small"
              type="email"
              value={values.contractor_email}
              onChange={set("contractor_email")}
              inputProps={{ "aria-label": "Contractor Email" }}
            />
          </Field>
          <Field label="Contractor Office Phone">
            <TextField
              fullWidth
              size="small"
              type="tel"
              placeholder="(555) 123-4567"
              value={values.contractor_office_phone}
              onChange={setPhone("contractor_office_phone")}
              inputProps={{ "aria-label": "Contractor Office Phone" }}
            />
          </Field>
          <Field label="Contractor Cell Phone">
            <TextField
              fullWidth
              size="small"
              type="tel"
              placeholder="(555) 123-4567"
              value={values.contractor_cell_phone}
              onChange={setPhone("contractor_cell_phone")}
              inputProps={{ "aria-label": "Contractor Cell Phone" }}
            />
          </Field>
        </div>
      </Section>

      {/* The issue */}
      <Section plain={plain} title="The issue">
        <Field
          label="Describe Issue"
          required
          hint="What's wrong, which unit, and anything the technician should know before going out."
        >
          <TextField
            fullWidth
            multiline
            minRows={5}
            value={values.describe_issue}
            onChange={set("describe_issue")}
            inputProps={{ "aria-label": "Describe Issue" }}
          />
        </Field>

        {/* Photos */}
        <Field
          label="Photos"
          hint={`Up to ${MAX_FILES} files, 64 MB each. Photos of the issue save the technician a trip.`}
        >
          <label className="app-surface flex cursor-pointer flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed px-4 py-8 text-center transition hover:border-sky-400">
            <AddPhotoAlternateIcon className="app-muted" />
            <span className="app-muted text-sm">
              Drop files here or click to select
            </span>
            <input
              type="file"
              multiple
              accept="image/*,application/pdf"
              className="hidden"
              onChange={addFiles}
            />
          </label>
          {files.length > 0 && (
            <ul className="mt-3 space-y-2">
              {files.map((file, index) => (
                <li
                  key={`${file.name}-${index}`}
                  className="app-surface flex items-center justify-between gap-3 rounded-xl border py-1 pl-3 pr-1 text-sm"
                >
                  <span className="truncate">{file.name}</span>
                  <Tooltip title="Remove">
                    <button
                      type="button"
                      aria-label={`Remove ${file.name}`}
                      onClick={() => removeFile(index)}
                      className="app-icon-btn shrink-0 hover:!text-rose-500"
                    >
                      <CloseIcon fontSize="small" />
                    </button>
                  </Tooltip>
                </li>
              ))}
            </ul>
          )}
        </Field>
      </Section>

      {/* Billing */}
      <Section plain={plain} title="Billing">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Field label="Bill To" required>
            <TextField
              select
              fullWidth
              size="small"
              value={values.bill_to}
              onChange={set("bill_to")}
              SelectProps={{
                displayEmpty: true,
                SelectDisplayProps: { "aria-label": "Bill To" },
              }}
            >
              <MenuItem value="" disabled>
                Select...
              </MenuItem>
              {BILL_TO_OPTIONS.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </TextField>
          </Field>
          <Field label="Location">
            <TextField
              select
              fullWidth
              size="small"
              value={values.location}
              onChange={set("location")}
              SelectProps={{
                displayEmpty: true,
                SelectDisplayProps: { "aria-label": "Location" },
              }}
            >
              <MenuItem value="">None</MenuItem>
              {branches
                .filter((branch) => branch !== "All Locations")
                .map((branch) => (
                  <MenuItem key={branch} value={branch}>
                    {branch}
                  </MenuItem>
                ))}
            </TextField>
          </Field>
          <Field label="Account Number">
            <TextField
              fullWidth
              size="small"
              value={values.account_number}
              onChange={set("account_number")}
              inputProps={{ "aria-label": "Account Number" }}
            />
          </Field>
          <Field label="Job Number">
            <TextField
              fullWidth
              size="small"
              value={values.job_number}
              onChange={set("job_number")}
              inputProps={{ "aria-label": "Job Number" }}
            />
          </Field>
        </div>
      </Section>

      {error && (
        <div className={embedded ? "px-6 pb-2" : ""}>
          <div
            role="alert"
            className="rounded-xl bg-rose-500/10 px-4 py-3 text-sm text-rose-700 dark:text-rose-300"
          >
            {error}
          </div>
        </div>
      )}

      <div
        className={
          embedded
            ? "app-divider flex flex-wrap items-center justify-between gap-3 border-t px-6 py-4"
            : "app-card flex flex-wrap items-center justify-between gap-3 rounded-2xl border px-5 py-4"
        }
      >
        <Button
          type="button"
          variant="text"
          color="inherit"
          onClick={startAnother}
          disabled={submitting}
        >
          Clear form
        </Button>
        <Button
          type="submit"
          variant="contained"
          size="large"
          disabled={submitting}
          startIcon={
            submitting ? <CircularProgress size={16} color="inherit" /> : null
          }
        >
          {submitting ? "Submitting…" : "Submit Request"}
        </Button>
      </div>
    </form>
  );

  if (embedded) return formCard;

  return (
    <PageShell size="md">
      <PageHeader
        icon={BuildIcon}
        tone="teal"
        eyebrow={manual ? "Windows & Doors" : "Forms"}
        title={title}
        description={description}
      />
      {formCard}
    </PageShell>
  );
};

export default WindowServiceRequestForm;
